import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import {
  ORDER_FILTER_FORM_ID,
  SalesErrorCard,
  SalesParamsForm,
  SalesRatio,
} from '@/components/organisms/Sales';
import { HEADER_ID, PageLayout } from '@/components/templates/PageLayout';
import {
  CmBox,
  CmLongLoading,
  CmMessageCard,
  CmScrollBox,
  CmSimpleGrid,
} from '@/components/uiParts';
import { PieDatum } from '@/domain/models/Chart';
import { useTargetsDomHeight } from '@/hooks/Dom';
import { redirect404Page } from '@/lib/AccessControl';
import { errorHandler } from '@/lib/ErrorHandler';
import { SalesCard } from '@/pages/sales_report/const';
import { routes } from '@/router';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import { useDeliveryServiceLinkEnterprise } from '@/services/Enterprise/DeliveryService/hooks';
import { useEnterprise } from '@/services/Enterprise/hooks';
import { useItemsSalesFilter } from '@/services/SalesReport/hooks';

import { CountRatioByItem } from './components/CountRatioByItem';

const SalesItemsPage: React.FC = () => {
  const history = useHistory();
  const { managerAccountMe: me } = useManagerAccountMe();
  /* アクセス制御 */
  redirect404Page('sales_report', history, me);
  const { salesItem, isLoading, error, filter, setFilter } =
    useItemsSalesFilter();

  if (error) {
    errorHandler({ error });
  }

  const {
    enterprise,
    isLoading: isEnterpriseLoading,
    isError: isEnterpriseError,
  } = useEnterprise();

  const {
    deliveryServices,
    isLoading: isDeliveryServiceLoading,
    isError: isDeliveryServiceError,
  } = useDeliveryServiceLinkEnterprise();

  const { noData } = SalesCard;

  const itemDisplayData: PieDatum[] = useMemo(() => {
    if (!salesItem || !salesItem.item_sales) return [];
    const totalPriceData = salesItem.item_sales.map((item) => ({
      id: item.item_name,
      value: item.total_sales,
    }));
    return totalPriceData;
  }, [salesItem]);

  const { targetsHeight } = useTargetsDomHeight([
    HEADER_ID,
    ORDER_FILTER_FORM_ID,
  ]);

  return (
    <PageLayout title={routes.ItemsReport.title}>
      <CmBox>
        {enterprise && deliveryServices && (
          <SalesParamsForm
            filter={filter}
            enterprise={enterprise}
            deliveryServices={deliveryServices}
            handleSubmit={setFilter}
            orderCount={salesItem?.total_order_count ?? 0}
            page="items"
          />
        )}
        <CmScrollBox w="100%" height={`calc(100vh - ${targetsHeight}px)`}>
          {salesItem && salesItem.total_sales > 0 ? (
            <CmBox>
              <CmSimpleGrid
                columns={{ base: 1, md: 2 }}
                spacingX={3}
                spacingY={5}
              >
                <SalesRatio
                  displayData={itemDisplayData}
                  title="商品ごと売上"
                  isEmpty={salesItem.total_sales === 0}
                />
                <CountRatioByItem items={salesItem.item_sales} />
              </CmSimpleGrid>
            </CmBox>
          ) : (
            !isLoading &&
            !isEnterpriseLoading && (
              <CmMessageCard margin="100px auto 0">
                {noData.message}
              </CmMessageCard>
            )
          )}
        </CmScrollBox>
      </CmBox>

      {(isLoading || isEnterpriseLoading || isDeliveryServiceLoading) && (
        <CmLongLoading />
      )}

      {(isEnterpriseError || isDeliveryServiceError) && <SalesErrorCard />}
    </PageLayout>
  );
};

export default SalesItemsPage;
